/**
 * R77 · Sorted + searchable "Add from activity master…" combobox.
 * Uses cmdk under the hood via Command primitives so it does live-filter
 * as the user types. Items are pre-sorted A→Z on name.
 */
import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Plus } from "lucide-react";

export default function ActivityMasterCombobox({ items, onPick, testid = "pkg-add-from-master" }) {
  const [open, setOpen] = useState(false);
  const sorted = useMemo(
    () => [...(items || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [items],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 w-64 justify-between rounded-none text-xs"
          data-testid={testid}
        >
          <span className="inline-flex items-center gap-1.5 truncate">
            <Plus className="w-3.5 h-3.5" />
            Add from activity master…
          </span>
          <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 rounded-none" align="start">
        <Command>
          <CommandInput
            placeholder="Search activities…"
            className="text-xs"
            data-testid={`${testid}-search`}
          />
          <CommandList>
            <CommandEmpty>No activity matches.</CommandEmpty>
            <CommandGroup>
              {sorted.map((a) => (
                <CommandItem
                  key={a.id}
                  value={`${a.name || ""} ${a.category || ""}`}
                  onSelect={() => {
                    onPick(a.id);
                    setOpen(false);
                  }}
                  data-testid={`${testid}-option-${a.id}`}
                  className="text-xs"
                >
                  <div className="flex-1 truncate">
                    <div className="font-medium truncate">{a.name}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {a.category} · {a.is_external ? "external" : "internal"}
                    </div>
                  </div>
                  <Check className="w-3.5 h-3.5 opacity-0" />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
